import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { Anvil, Search, MapPin, Star, ShieldCheck, Map as MapIcon, ArrowRight } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function PublicDirectory() {
  const [items, setItems] = useState([]);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [sts, setSts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/public/directory`, { params: { q, city, specialty, sts: sts ? "1" : "" } })
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Annuaire des selliers-garnisseurs | Fabriko";
    axios.get(`${API}/public/directory/specialties`).then((r) => setSpecialtiesList(r.data)).catch(() => {});
    axios.get(`${API}/public/cities`).then((r) => setCities(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, city, specialty, sts]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/annuaire" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-sky-400 flex items-center justify-center"><Anvil className="w-5 h-5 text-navy" /></div>
            <span className="font-display font-bold text-lg">Fabriko · Annuaire</span>
          </Link>
          <Link to="/reseau-carte" data-testid="link-network-map"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-400 text-navy text-sm font-semibold hover:bg-sky-500 transition-colors">
            <MapIcon className="w-4 h-4" /> Carte du réseau STS
          </Link>
        </div>
      </header>

      <section className="bg-navy-deep text-white">
        <div className="max-w-6xl mx-auto px-5 py-12">
          <h1 className="text-3xl sm:text-4xl font-display font-bold">Trouvez votre sellier-garnisseur</h1>
          <p className="text-slate-300 mt-2 max-w-2xl">Artisans selliers, bâchiers et garnisseurs partout en France. Avis clients vérifiés sur 4 critères.</p>
          <div className="mt-6 grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2 flex items-center gap-2 bg-white rounded-xl px-3 py-2.5">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dir-search" placeholder="Nom, spécialité…"
                className="flex-1 text-navy text-sm outline-none" />
            </div>
            <input value={city} onChange={(e) => setCity(e.target.value)} data-testid="dir-city" placeholder="Ville"
              className="bg-white rounded-xl px-3 py-2.5 text-navy text-sm outline-none" />
            <select value={specialty} onChange={(e) => setSpecialty(e.target.value)} data-testid="dir-specialty"
              className="bg-white rounded-xl px-3 py-2.5 text-navy text-sm outline-none">
              <option value="">Toutes spécialités</option>
              {specialtiesList.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="dir-sts-toggle">
            <input type="checkbox" checked={sts} onChange={(e) => setSts(e.target.checked)} className="accent-sky-400 w-4 h-4" />
            <ShieldCheck className="w-4 h-4 text-sky-400" /> Membres du réseau STS uniquement
          </label>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 py-10">
        {cities.length > 0 && (
          <div className="mb-8" data-testid="popular-cities">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Villes populaires</h2>
            <div className="flex flex-wrap gap-2">
              {cities.slice(0, 12).map((c) => (
                <Link key={c.city} to={`/selliers/${encodeURIComponent(c.city)}`} data-testid={`city-link-${c.city}`}
                  className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-navy hover:border-sky-300 transition-colors">
                  {c.city} <span className="text-slate-400">({c.count})</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {loading ? (
          <p className="text-center text-slate-400 py-16">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-400 py-16" data-testid="dir-empty">Aucun artisan trouvé.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dir-grid">
            {items.map((a) => (
              <Link key={a.id} to={`/annuaire/${a.id}`} data-testid={`dir-card-${a.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-sky-300 transition-all flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-navy text-sky-300 flex items-center justify-center font-display font-bold shrink-0">{initials(a.name)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-semibold text-navy leading-tight truncate">{a.name}</h3>
                      {a.member_sts && <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold" title="Membre du réseau STS"><ShieldCheck className="w-3 h-3" /> STS</span>}
                    </div>
                    {a.city && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {a.city}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Stars value={a.rating} />
                  <span className="text-sm font-semibold text-navy">{a.rating || "—"}</span>
                  <span className="text-xs text-slate-400">({a.reviews_count} avis)</span>
                </div>
                {a.specialties?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {a.specialties.slice(0, 3).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">{s}</span>)}
                  </div>
                )}
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-sky-600 font-semibold">Voir la fiche <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-navy text-slate-300 mt-10">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm">Annuaire des selliers-garnisseurs</span>
          <Link to="/login" className="inline-flex items-center gap-2 text-sky-400 text-sm hover:underline">Propulsé par Fabriko <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </footer>
    </div>
  );
}
